/**
 * Calculator Input Builder
 * Assembles the input object that dashboard calculators expect,
 * given a user's data and client configuration.
 *
 * Extracted from index.js getEquityData() for reuse in
 * custom reports and cap table columns.
 */

import {
	isUserTransaction,
	getActiveUserIds,
	getEffectiveStrikePriceForUser,
	calculateVestedFromSchedule,
} from './equity-calculation-utils.js';
import { getCAGR } from './string-utils.js';

/**
 * Aggregate user events against client events to compute
 * bases, distributions, contributions, returnsOfCapital, and incomeAllocations
 * per share class.
 *
 * @param {Object} userShares - userClient.shares object (keyed by class)
 * @param {Object} equity - clientData.equity object (keyed by class)
 * @param {string[]} shareClasses - Array of share class keys
 * @param {string|string[]} userIds - User ID(s) for transaction filtering
 * @returns {Object} { bases, distributions, contributions, returnsOfCapital, incomeAllocations }
 */
/**
 * Compute net transfer amounts per share class from transactions.
 * @param {Object} equity - clientData.equity
 * @param {string[]} shareClasses - Array of share class keys
 * @param {string|string[]} userIds - User ID(s) for filtering
 * @returns {Object} Net transfers keyed by share class (positive = received, negative = sent)
 */
export function computeNetTransfers(equity, shareClasses, userIds) {
	const transfers = {};
	const normalizedIds = (Array.isArray(userIds) ? userIds : [userIds])
		.map((id) => id?.toLowerCase())
		.filter(Boolean);

	shareClasses.forEach((c) => {
		transfers[c] = 0;
		if (equity[c]?.transactions) {
			equity[c].transactions.forEach((t) => {
				const amount = (Number(t.quantity) || 0) * (Number(t.sharePrice) || 0);
				if (!amount) return;
				const senderLower = t.sender?.toLowerCase();
				const receiverLower = t.receiver?.toLowerCase();
				const isSender = senderLower && normalizedIds.includes(senderLower);
				const isReceiver = receiverLower && normalizedIds.includes(receiverLower);
				if (isReceiver && !isSender) transfers[c] += amount;
				else if (isSender && !isReceiver) transfers[c] -= amount;
			});
		}
	});
	return transfers;
}

export function aggregateEventData(userShares, equity, shareClasses, userIds) {
	const bases = {};
	const distributions = {};
	const contributions = {};
	const returnsOfCapital = {};
	const incomeAllocations = {};
	const accruals = {};
	const transfers = computeNetTransfers(equity, shareClasses, userIds);

	let userEvents = [];
	let clientEvents = [];

	shareClasses.forEach((c) => {
		bases[c] = -1;
		distributions[c] = 0;
		contributions[c] = 0;
		returnsOfCapital[c] = 0;
		incomeAllocations[c] = 0;
		accruals[c] = 0;

		if (userShares[c]?.events) {
			userShares[c].events.forEach((e) => userEvents.push(e));
		}
		if (equity[c]?.events) {
			equity[c].events.forEach((e) => {
				clientEvents.push({ ...e, shareClass: c });
			});
		}
	});

	// Process events to compute bases and aggregations
	userEvents.forEach((e) => {
		const clientEvent = clientEvents.find((obj) => obj.nrid == e.nrid);
		if (!clientEvent) return;

		const grossDollars = Number(e.grossDollars);
		const sc = clientEvent.shareClass;
		if (bases[sc] == -1 && ['Contribution', 'Redemption', 'Return of Capital'].includes(clientEvent.eventType)) {
			bases[sc] = 0;
		}
		const includeClassROC = equity[sc]?.includeROC;

		switch (clientEvent.eventType) {
			case 'Distribution':
			case 'Cash Dividend':
				distributions[sc] += grossDollars;
				break;
			case 'Contribution':
				bases[sc] += grossDollars;
				contributions[sc] += grossDollars;
				break;
			case 'Redemption':
				bases[sc] -= grossDollars;
				break;
			case 'Return of Capital':
				if (includeClassROC !== false) {
					bases[sc] -= grossDollars;
				}
				returnsOfCapital[sc] += grossDollars;
				break;
			case 'Gain (Loss) Accrual':
				accruals[sc] += grossDollars;
				break;
		}
	});

	return { bases, distributions, contributions, returnsOfCapital, incomeAllocations, accruals, transfers };
}

/**
 * Get the latest share price from a class's valuations array.
 * @param {Object} equityClass - clientData.equity[shareClass]
 * @returns {{ latest: number, prior: number }} Latest and prior share prices
 */
function getSharePrices(equityClass) {
	if (!equityClass?.valuations?.length) return { latest: 0, prior: 0 };
	const sorted = equityClass.valuations.slice().sort((a, b) => Number(b.date) - Number(a.date));
	return {
		latest: Number(sorted[0]?.sharePrice) || 0,
		prior: Number(sorted[1]?.sharePrice) || 0,
	};
}

/**
 * Calculate total fund contributions and distributions from client-level events.
 * @param {Object} equityClass - clientData.equity[shareClass]
 * @returns {{ fundTotalContributions: number, fundTotalDistributions: number }}
 */
function getFundEventTotals(equityClass) {
	let fundTotalContributions = 0;
	let fundTotalDistributions = 0;
	if (!equityClass?.events?.length) return { fundTotalContributions, fundTotalDistributions };

	equityClass.events.forEach((event) => {
		if (event.eventType === 'Contribution') {
			fundTotalContributions += Number(event.grossDollars) || 0;
		} else if (event.eventType === 'Distribution') {
			fundTotalDistributions += Number(event.grossDollars) || 0;
		}
	});
	return { fundTotalContributions, fundTotalDistributions };
}

/**
 * Calculate totalTarget from a vesting object (BW-style).
 * Sums schedule amounts and goal amounts, skipping cashedOut/forfeited entries.
 * @param {Object} vestingObj - User's vesting object for a class
 * @returns {number} Total target shares (0 if no vesting)
 */
function getTotalTarget(vestingObj) {
	if (!vestingObj || typeof vestingObj !== 'object') return 0;
	let totalTarget = 0;
	Object.values(vestingObj).forEach((entry) => {
		if (entry?.cashedOut === true) return;
		if (entry?.schedule) {
			Object.values(entry.schedule).forEach((item) => {
				if (item?.forfeited === true) return;
				totalTarget += Number(item?.amount || 0);
			});
		}
		if (entry?.goals) {
			Object.values(entry.goals).forEach((goal) => {
				if (goal?.forfeited === true) return;
				totalTarget += Number(goal?.amount || 0);
			});
		}
	});
	return totalTarget;
}

/**
 * Find the underlying stock price for option-type classes without their own valuations.
 * @param {Object} equityClass - clientData.equity[shareClass]
 * @param {Object} allEquity - clientData.equity (all classes)
 * @returns {number} Share price from underlying class, or 0
 */
function getUnderlyingSharePrice(equityClass, allEquity) {
	// First try explicit underlyingClass if defined
	let underlyingEquity = equityClass?.underlyingClass ? allEquity[equityClass.underlyingClass] : null;

	// Fallback: find first share class with valuations that isn't an option type
	if (!underlyingEquity?.valuations?.length) {
		const stockClasses = Object.keys(allEquity).filter((cls) => {
			const eq = allEquity[cls];
			return eq?.valuations?.length > 0 && !eq?.useStrike;
		});
		if (stockClasses.length > 0) {
			underlyingEquity = allEquity[stockClasses[0]];
		}
	}

	if (!underlyingEquity?.valuations?.length) return 0;
	const sorted = underlyingEquity.valuations.slice().sort((a, b) => Number(b.date) - Number(a.date));
	return Number(sorted[0]?.sharePrice) || 0;
}

/**
 * Convert TVPI multipliers to raw dollar NAV for fund classes.
 * TVPI = (NAV + Distributions) / Contributions, so NAV = TVPI * Contributions - Distributions
 *
 * @param {Object} holdings - The calculator input holdings object (mutated in place)
 * @param {string[]} shareClasses - Share class keys
 * @param {Object} equity - clientData.equity
 */
function convertTvpiToNav(holdings, shareClasses, equity) {
	shareClasses.forEach((k) => {
		const tvpi = holdings.fundNav[k] || 0;
		const fundTotal = holdings.fundTotalContributions[k] || 0;
		if (tvpi > 0 && fundTotal > 0) {
			const keyEquity = equity[k];
			let valDate = Infinity;
			if (keyEquity?.valuations?.length) {
				const sorted = keyEquity.valuations.slice().sort((a, b) => Number(b.date) - Number(a.date));
				valDate = Number(sorted[0].date) || Infinity;
			}
			// Only include distributions through the valuation date
			let fundDist = 0;
			if (keyEquity?.events?.length) {
				keyEquity.events.forEach((event) => {
					if (event.eventType === 'Distribution' && Number(event.date) <= valDate) {
						fundDist += Number(event.grossDollars) || 0;
					}
				});
			}
			holdings.fundNav[k] = tvpi * fundTotal - fundDist;
		}
	});
}

/**
 * Detect if the client uses TVPI-style valuations (where sharePrice is a multiplier, not dollars).
 * @param {Object} clientData - Client configuration
 * @returns {boolean}
 */
export function isTvpiValuation(clientData) {
	const labelValuation = clientData?.language?.label_valuation || '';
	const labelTvpi = clientData?.language?.label_tvpi || '';
	return labelValuation.startsWith('%') || labelValuation.toUpperCase().includes('TVPI') || labelTvpi.toUpperCase().includes('TVPI');
}

/**
 * Build the calculator input object for a single user.
 * This produces the same shape consumed by all dashboard calculators
 * (PrivateCompany409A, FundInvestor, ContributionsAccruals, etc.)
 *
 * @param {Object} params
 * @param {Object} params.userShares - userClient.shares object (keyed by class)
 * @param {Object} params.clientData - Full client configuration
 * @param {string[]} params.shareClasses - Array of share class keys to include
 * @param {string|string[]} params.userIds - User ID(s) for transaction/event filtering
 * @param {Object} [params.eventData] - Pre-computed event aggregations (if already available).
 *   If omitted, will be computed via aggregateEventData().
 *   Shape: { bases, distributions, contributions, returnsOfCapital, incomeAllocations }
 * @param {Object} [params.options] - Calculator options overrides
 * @param {boolean} [params.options.floorAtZero=false] - Floor negative values at zero
 * @param {number} [params.options.percentagePrecision] - Decimal precision for fund %
 * @param {boolean} [params.options.zeroStrikePrice=false] - Force strike price to 0 (BW-style)
 * @returns {Object} Calculator input object
 */
export function buildCalculatorInput({ userShares, clientData, shareClasses, userIds, eventData, options = {} }) {
	if (!userShares || !clientData?.equity || !shareClasses?.length) {
		return null;
	}

	// Compute event aggregations if not provided
	const events = eventData || aggregateEventData(userShares, clientData.equity, shareClasses, userIds);

	// Compute transfers if not already in event data (pre-computed eventData from index.js omits them)
	if (!events.transfers) {
		events.transfers = computeNetTransfers(clientData.equity, shareClasses, userIds);
	}

	const holdings = {
		owned: {},
		vested: {},
		totalTarget: {},
		sharePrice: {},
		priorSharePrice: {},
		strikePrice: {},
		tvpi: {},
		fundNav: {},
		fundTotalContributions: {},
		fundTotalDistributions: {},
		contributions: events.contributions,
		distributions: events.distributions,
		capitalAccount: events.bases,
		returnsOfCapital: events.returnsOfCapital,
		accruals: events.accruals,
		transfers: events.transfers || {},
		bases: events.bases,
		incomeAllocations: events.incomeAllocations,
	};

	// Populate per-class data
	shareClasses.forEach((k) => {
		const classShares = userShares[k];
		const owned = Number(classShares?.owned || 0);
		holdings.owned[k] = owned;

		// Vested shares
		const scheduleVested = classShares?.vesting
			? calculateVestedFromSchedule(classShares.vesting, owned)
			: Number(classShares?.approved ?? owned);
		holdings.vested[k] = Math.min(scheduleVested, owned);

		// Total target (BW-style vesting)
		if (classShares?.vesting) {
			const totalTarget = getTotalTarget(classShares.vesting);
			if (totalTarget > 0) {
				holdings.totalTarget[k] = totalTarget;
			}
		}

		const equityClass = clientData.equity[k];
		const hasStrikePriceType = equityClass?.useStrike === true;

		// Share prices from valuations
		const prices = getSharePrices(equityClass);
		if (prices.latest > 0) {
			holdings.sharePrice[k] = prices.latest;
			holdings.tvpi[k] = prices.latest;
			holdings.fundNav[k] = prices.latest;
			if (prices.prior > 0) {
				holdings.priorSharePrice[k] = prices.prior;
			}
		}

		// For options without their own valuations, use underlying stock price
		if (hasStrikePriceType && !holdings.sharePrice[k]) {
			const underlyingPrice = getUnderlyingSharePrice(equityClass, clientData.equity);
			if (underlyingPrice > 0) {
				holdings.sharePrice[k] = underlyingPrice;
			}
		}

		// Fund event totals
		const fundTotals = getFundEventTotals(equityClass);
		holdings.fundTotalContributions[k] = fundTotals.fundTotalContributions;
		holdings.fundTotalDistributions[k] = fundTotals.fundTotalDistributions;

		// Strike price
		if (options.zeroStrikePrice) {
			holdings.strikePrice[k] = 0;
		} else {
			holdings.strikePrice[k] = hasStrikePriceType
				? getEffectiveStrikePriceForUser(equityClass, userIds, {
						useWeighted: true,
						netContra: true,
						outstandingOnly: true,
					})
				: 0;
		}
	});

	// Convert TVPI to raw NAV if client uses TVPI-style valuations
	const useTvpi = isTvpiValuation(clientData);
	if (useTvpi) {
		convertTvpiToNav(holdings, shareClasses, clientData.equity);
	}

	return {
		holdings,
		shareClasses,
		options: {
			floorAtZero: options.floorAtZero || false,
			percentagePrecision: useTvpi ? 10 : (options.percentagePrecision ?? clientData.flags?.percentage_precision ?? 4),
			useFundLanguage: clientData.use_fund_language || false,
		},
	};
}

/**
 * Compute CAGR (Compound Annual Growth Rate) for calculator results.
 * Adds per-class `cagr` and portfolio-level `cagr` to the calculationResult.
 *
 * @param {Object} calculationResult - Result from a calculator's calculate() method
 * @param {Object} clientData - Client configuration (needs equity[k].events)
 * @param {Object} userShares - userClient.shares (needs [k].events)
 * @param {Object} [options]
 * @param {boolean} [options.useCurrentTimeForClassCagr=true] - Use current time as end date for per-class CAGR
 * @param {boolean} [options.useCurrentTimeForPortfolioCagr=true] - Use current time as end date for portfolio CAGR
 */
export function computeCAGR(calculationResult, clientData, userShares, options) {
	if (!calculationResult?.breakdown?.length) return;

	const useCurrentForClass = options?.useCurrentTimeForClassCagr !== false;
	const useCurrentForPortfolio = options?.useCurrentTimeForPortfolioCagr !== false;

	// Per-class CAGR
	calculationResult.breakdown.forEach(function (classData) {
		if (classData.value <= 0) return;
		const k = classData.classKey;
		const clEvents = clientData.equity[k]?.events || [];
		const usEvents = userShares[k]?.events || [];
		if (!usEvents.length || !clEvents.length) return;

		const matched = [];
		usEvents.forEach(function (ue) {
			const ce = clEvents.find(function (c) { return c.nrid === ue.nrid; });
			if (ce && ce.date) {
				matched.push({ date: Number(ce.date), grossDollars: Number(ue.grossDollars) || 0 });
			}
		});
		if (matched.length < 2) return;
		matched.sort(function (a, b) { return a.date - b.date; });

		const firstValue = Math.abs(matched[0].grossDollars);
		if (firstValue > 0) {
			const endDate = useCurrentForClass ? Math.floor(Date.now() / 1000) : matched[matched.length - 1].date;
			classData.cagr = getCAGR(firstValue, classData.value, matched[0].date, endDate);
		}
	});

	// Portfolio-level CAGR
	let earliestDate = Infinity;
	let latestDate = 0;
	let earliestValue = 0;
	calculationResult.breakdown.forEach(function (classData) {
		const k = classData.classKey;
		const clEvents = clientData.equity[k]?.events || [];
		const usEvents = userShares[k]?.events || [];
		usEvents.forEach(function (ue) {
			const ce = clEvents.find(function (c) { return c.nrid === ue.nrid; });
			if (ce && ce.date) {
				const d = Number(ce.date);
				if (d < earliestDate) {
					earliestDate = d;
					earliestValue = Math.abs(Number(ue.grossDollars) || 0);
				}
				if (d > latestDate) latestDate = d;
			}
		});
	});

	const startValue = calculationResult.totalContributions > 0
		? calculationResult.totalContributions : earliestValue;
	const endDate = useCurrentForPortfolio ? Math.floor(Date.now() / 1000) : latestDate;
	if (earliestDate < Infinity && endDate > earliestDate && startValue > 0 && calculationResult.totalValue > 0) {
		calculationResult.cagr = getCAGR(startValue, calculationResult.totalValue, earliestDate, endDate);
	}
}
